import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Radio } from '@creditornot/cb-radio';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { usePluralization } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { CampaignRulesFormValues } from 'modules/campaigns/types';

import { shouldPreventChangingMinimalBasketRequirement } from './utils';
import {
  telemetryNewCampaignInteractionClickCampaignRulesBasket,
  telemetryNewCampaignRulesInteractionClickUserRules,
  telemetryUpdateCampaignInteractionClickCampaignRulesBasket,
  telemetryUpdateCampaignRulesInteractionClickUserRules,
  useTelemetryNewCampaignRulesView,
} from '../../utils/telemetry/telemetry';
import CustomRadioCard from '../CustomRadioCard';

export type RulesFormModalProps = {
  onClose: () => void;
  onUpdate: () => void;
  minBasketSizeOptions?: number[];
  campaignType: CampaignType;
  isNewCampaignRoute?: boolean;
};

const StyledModalHeader = styled(ModalHeader)`
  padding: 16px;
`;

const ModalContainer = styled.div`
  background-color: ${cbSemanticCssVars.surfaceMain};
  min-width: 800px;
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const USER_LIMIT_TIMES = [1, 2, 3];

const RadioGroup = styled.div<{ isColumn?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: ${({ isColumn }) => (isColumn ? 'flex-start' : 'center')};
  justify-content: ${({ isColumn }) => (isColumn ? 'flex-start' : 'center')};
  flex-direction: ${({ isColumn }) => (isColumn ? 'column' : 'row')};
  gap: 8px;
`;

const StyledSection = styled.div`
  padding: 16px;

  &:not(:last-of-type) {
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledTitle = styled(Typography.Body2).attrs({ fontWeight: 'bold' })`
  margin-block-end: 16px;
  display: block;
`;

const ActionsFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledTag = styled(Tag)`
  letter-spacing: 0.5px;
  line-height: 1em;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const RulesFormModal = ({
  onClose,
  onUpdate,
  minBasketSizeOptions = [],
  campaignType,
  isNewCampaignRoute = false,
}: RulesFormModalProps) => {
  const { currency, country } = useCampaignModuleContext();
  const preventChangingMinimalBasketRequirement = shouldPreventChangingMinimalBasketRequirement(country, campaignType);
  const intl = useIntl();
  const campaignRulesForm = useForm<CampaignRulesFormValues>();
  const formValues = campaignRulesForm.getState().values;

  const { FormattedPluralMessage } = usePluralization();

  const handleMinBasketSizeTelemetry = (basketSize: number) => {
    const size = Number(convertMonetaryIntToDecimal(basketSize, currency)).toLocaleString(intl.locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    });

    if (isNewCampaignRoute) {
      telemetryNewCampaignInteractionClickCampaignRulesBasket(size, campaignType);
    } else {
      telemetryUpdateCampaignInteractionClickCampaignRulesBasket(size, campaignType);
    }
  };

  const handleFirstPurchaseOnlyTelemetry = (isFirstTimeShoppers: CampaignRulesFormValues['firstPurchaseOnly']) => {
    const telemetryValue = isFirstTimeShoppers ? 'first_time_shoppers' : 'everybody';
    if (isNewCampaignRoute) {
      telemetryNewCampaignRulesInteractionClickUserRules(telemetryValue, campaignType);
    } else {
      telemetryUpdateCampaignRulesInteractionClickUserRules(telemetryValue, campaignType);
    }
  };

  useTelemetryNewCampaignRulesView(campaignType, isNewCampaignRoute);

  const sortedMinBasketSizeOptions = minBasketSizeOptions.sort((a, b) => a - b);
  const [smallestMinBasketSizeOption] = sortedMinBasketSizeOptions;
  const increasedBasketSizeOptions = sortedMinBasketSizeOptions.slice(1);
  const withBasketSize = minBasketSizeOptions.length > 0;

  return (
    <ModalContainer>
      <StyledModalHeader onCloseClick={onClose}>
        <Typography.Heading6>
          {withBasketSize ? (
            <FormattedMessage id="campaigns.form.rules.title.audience-and-size" />
          ) : (
            <FormattedMessage id="campaigns.form.rules.title.audience" />
          )}
        </Typography.Heading6>
      </StyledModalHeader>
      <StyledModalContent>
        <StyledSection>
          <StyledTitle>
            <FormattedMessage id="campaigns.form.audience.label" />
          </StyledTitle>
          <RadioGroup>
            <Field<CampaignRulesFormValues['firstPurchaseOnly']> name="firstPurchaseOnly" type="radio" value={false}>
              {({ input }) => (
                <CustomRadioCard
                  {...input}
                  value={input.value?.toString()}
                  onChange={() => {
                    handleFirstPurchaseOnlyTelemetry(input.value);
                    input.onChange(input.value);
                    campaignRulesForm.change('usageType', 'indefinitely');
                  }}
                >
                  <Typography.Body3>
                    <FormattedMessage id="campaigns.form.audience.everybody" />
                  </Typography.Body3>
                </CustomRadioCard>
              )}
            </Field>
            <Field<CampaignRulesFormValues['firstPurchaseOnly']> name="firstPurchaseOnly" type="radio" value>
              {({ input }) => (
                <CustomRadioCard
                  {...input}
                  value={input.value?.toString()}
                  onChange={() => {
                    handleFirstPurchaseOnlyTelemetry(input.value);
                    input.onChange(input.value);
                    campaignRulesForm.change('usageType', undefined);
                    campaignRulesForm.change('perUserUsageLimit', undefined);
                  }}
                >
                  <Typography.Body3>
                    <FormattedMessage id="campaigns.form.audience.first-time" />
                  </Typography.Body3>
                </CustomRadioCard>
              )}
            </Field>
          </RadioGroup>
        </StyledSection>

        {!formValues.firstPurchaseOnly && (
          <StyledSection>
            <StyledTitle>
              <FormattedMessage id="campaigns.form.user-limit.label" />
            </StyledTitle>
            <RadioGroup>
              <Field<CampaignRulesFormValues['usageType']> name="usageType" type="radio" value="indefinitely">
                {({ input }) => (
                  <CustomRadioCard
                    {...input}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                      input.onChange(evt);
                      campaignRulesForm.change('perUserUsageLimit', undefined);
                    }}
                  >
                    <Typography.Body3>
                      <FormattedMessage id="campaigns.form.user-limit.indefinitely" />
                    </Typography.Body3>
                  </CustomRadioCard>
                )}
              </Field>
              <Field<CampaignRulesFormValues['usageType']> name="usageType" type="radio" value="limited">
                {({ input }) => (
                  <CustomRadioCard
                    {...input}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                      input.onChange(evt);
                      campaignRulesForm.change('perUserUsageLimit', 1);
                    }}
                  >
                    <Typography.Body3>
                      <FormattedMessage id="campaigns.form.user-limit.limited" />
                    </Typography.Body3>
                  </CustomRadioCard>
                )}
              </Field>
            </RadioGroup>
          </StyledSection>
        )}

        {formValues.usageType === 'limited' && (
          <StyledSection>
            <StyledTitle>
              <FormattedMessage id="campaigns.form.user-limit.label-limit" />
            </StyledTitle>
            <RadioGroup>
              {USER_LIMIT_TIMES.map(option => (
                <Field<CampaignRulesFormValues['perUserUsageLimit']>
                  name="perUserUsageLimit"
                  type="radio"
                  value={option}
                  key={option}
                >
                  {({ input }) => (
                    <CustomRadioCard {...input} onChange={() => input.onChange(input.value)}>
                      <Typography.Body3>
                        <FormattedPluralMessage id="campaigns.form.user-limit.times" count={Number(option)} />
                      </Typography.Body3>
                    </CustomRadioCard>
                  )}
                </Field>
              ))}
            </RadioGroup>
          </StyledSection>
        )}

        {withBasketSize && (
          <>
            <StyledSection>
              <StyledTitle>
                <FormattedMessage id="campaigns.form.basket.label" />
              </StyledTitle>

              <RadioGroup isColumn>
                <Field<CampaignRulesFormValues['isMinBasketIncreased']>
                  name="isMinBasketIncreased"
                  type="radio"
                  value={false}
                >
                  {({ input }) => (
                    <Radio
                      {...input}
                      value={input.value?.toString()}
                      onChange={() => {
                        campaignRulesForm.change('minBasketSize', smallestMinBasketSizeOption);
                        input.onChange(input.value);

                        handleMinBasketSizeTelemetry(smallestMinBasketSizeOption);
                      }}
                      disabled={preventChangingMinimalBasketRequirement}
                    >
                      <StyledRow>
                        <Typography.Body3>
                          <FormattedMessage id="campaigns.form.basket.no" />
                        </Typography.Body3>
                        {!preventChangingMinimalBasketRequirement && (
                          <StyledTag variant="lightBlack">
                            <Typography.Overline4>
                              <FormattedMessage id="campaigns.recommended" />
                            </Typography.Overline4>
                          </StyledTag>
                        )}
                      </StyledRow>
                    </Radio>
                  )}
                </Field>

                <Field<CampaignRulesFormValues['isMinBasketIncreased']> name="isMinBasketIncreased" type="radio" value>
                  {({ input }) => (
                    <Radio
                      {...input}
                      value={input.value?.toString()}
                      onChange={() => {
                        campaignRulesForm.change('minBasketSize', increasedBasketSizeOptions[0]);
                        input.onChange(input.value);

                        handleMinBasketSizeTelemetry(increasedBasketSizeOptions[0]);
                      }}
                      disabled={preventChangingMinimalBasketRequirement || !increasedBasketSizeOptions.length}
                    >
                      <Typography.Body3>
                        <FormattedMessage id="campaigns.form.basket.yes" />
                      </Typography.Body3>
                    </Radio>
                  )}
                </Field>
              </RadioGroup>
            </StyledSection>

            {formValues.isMinBasketIncreased && (
              <StyledSection>
                <RadioGroup>
                  {increasedBasketSizeOptions.map(option => (
                    <Field<CampaignRulesFormValues['minBasketSize']>
                      name="minBasketSize"
                      type="radio"
                      value={option}
                      key={option}
                    >
                      {({ input }) => (
                        <CustomRadioCard
                          {...input}
                          onChange={() => {
                            input.onChange(input.value);

                            if (input.value !== undefined) {
                              handleMinBasketSizeTelemetry(input.value);
                            }
                          }}
                        >
                          {input.value && (
                            <Typography.Body3>
                              <MonetaryAmount
                                amount={convertMonetaryIntToDecimal(input.value, currency)}
                                currency={currency}
                              />
                            </Typography.Body3>
                          )}
                        </CustomRadioCard>
                      )}
                    </Field>
                  ))}
                </RadioGroup>
              </StyledSection>
            )}
          </>
        )}
      </StyledModalContent>

      <ActionsFooter>
        <Button variant="blue" size="small" onClick={onUpdate}>
          <FormattedMessage id="campaigns.form.update" />
        </Button>
      </ActionsFooter>
    </ModalContainer>
  );
};

export default RulesFormModal;
